import React, { useMemo, useState } from 'react'
import { ArrowLeft, RotateCw, BookOpen, Lightbulb } from 'lucide-react'
import { REFRANES } from './data.js'
import { tr, useLang } from '../lib/lang.js'
import { LangToggle } from '../lib/LangToggle.jsx'
import { rngFrom, shuffle } from '../lib/random.js'
import { awardBp } from '../lib/store.js'

const ROUND = 8

export function RefraneroGame({ onBack, onComplete }) {
  const [lang, setLang] = useLang()
  const [roundKey, setRoundKey] = useState(0)
  const [index, setIndex] = useState(0)
  const [score, setScore] = useState(0)
  const [locked, setLocked] = useState(null)
  const [showHint, setShowHint] = useState(false)
  const [done, setDone] = useState(false)

  const deck = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 39217)
    return shuffle(rng, REFRANES).slice(0, ROUND)
  }, [roundKey])

  const current = deck[index]

  const answer = (opt) => {
    if (locked != null) return
    const correct = opt === current.a
    setLocked({ correct, opt })
    if (correct) setScore((s) => s + 1)
    setTimeout(() => {
      if (index + 1 >= deck.length) {
        const finalScore = score + (correct ? 1 : 0)
        awardBp(finalScore * 12)
        onComplete?.(finalScore * 12)
        setDone(true)
      } else {
        setIndex((i) => i + 1)
        setLocked(null)
        setShowHint(false)
      }
    }, 750)
  }

  const restart = () => {
    setRoundKey((k) => k + 1)
    setIndex(0)
    setScore(0)
    setLocked(null)
    setShowHint(false)
    setDone(false)
  }

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[55vh] text-center space-y-5">
        <BookOpen size={40} className="text-amber-400" />
        <h2 className="text-xl font-black text-white">
          {score}/{deck.length}
        </h2>
        <p className="text-xs text-slate-400">
          {tr(lang, 'Refranes completados', 'Sayings completed')}
        </p>
        <div className="flex gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={restart}
            className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer flex items-center justify-center gap-2"
          >
            <RotateCw size={16} /> {tr(lang, 'Otra ronda', 'Another round')}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Salir', 'Exit')}
          </button>
        </div>
      </div>
    )
  }

  const parts = current.text.split('___')

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <LangToggle lang={lang} onChange={setLang} />
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-bold uppercase tracking-wider">El Refranero</span>
        <span>
          {index + 1}/{deck.length} · {score} ✓
        </span>
      </div>

      <div className="p-6 rounded-2xl bg-gradient-to-br from-[#1a1508] to-[#0d0c07] border border-amber-900/40 min-h-[26vh] flex flex-col justify-center">
        <p className="text-xl sm:text-2xl font-black text-white leading-relaxed text-center">
          {parts[0]}
          <span className="inline-block min-w-[4.5rem] mx-1 border-b-2 border-amber-500/60 text-amber-300 text-center align-baseline">
            {locked ? current.a : '· · ·'}
          </span>
          {parts[1]}
        </p>
        {lang === 'en' && current.en && (
          <p className="text-[11px] text-slate-500 text-center mt-3 italic">
            {tr(lang, '', current.en)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {current.options.map((opt) => {
          const isAnswer = opt === current.a
          const picked = locked?.opt === opt
          const cls =
            locked == null
              ? 'bg-[#131a29] border-slate-700 hover:border-amber-500/60 text-white'
              : isAnswer
                ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200'
                : picked
                  ? 'bg-rose-950/60 border-rose-500/50 text-rose-200'
                  : 'bg-[#0e131f] border-slate-800 text-slate-500'
          return (
            <button
              key={opt}
              type="button"
              disabled={locked != null}
              onClick={() => answer(opt)}
              className={`py-4 rounded-2xl border text-lg font-black transition-all cursor-pointer disabled:cursor-default ${cls}`}
            >
              {opt}
            </button>
          )
        })}
      </div>

      <button
        type="button"
        onClick={() => setShowHint((v) => !v)}
        className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-amber-300 cursor-pointer flex items-center justify-center gap-1.5"
      >
        <Lightbulb size={13} />
        {showHint ? current.en : tr(lang, 'Ver pista', 'Show hint')}
      </button>
    </div>
  )
}
